/**
 * Per-terminal POS ticket number minting — plan/28.
 * Pure helpers (no IDB) for Vitest.
 */

export type TerminalNumberRange = {
  nextSeq: number;
  rangeEnd: number;
  prefix: string;
  padWidth: number;
};

export function formatTerminalTicketNumber(
  range: Pick<TerminalNumberRange, "prefix" | "padWidth">,
  seq: number,
): string {
  const pad = Math.max(1, Math.min(12, range.padWidth));
  return `${range.prefix}${String(seq).padStart(pad, "0")}`;
}

export type MintResult =
  | { ok: true; number: string; nextRange: TerminalNumberRange }
  | { ok: false; reason: "exhausted" | "missing" };

/** Mint one ticket number and advance nextSeq (local cursor). */
export function mintTerminalTicketNumber(
  range: TerminalNumberRange | null | undefined,
): MintResult {
  if (!range) return { ok: false, reason: "missing" };
  if (range.nextSeq > range.rangeEnd) {
    return { ok: false, reason: "exhausted" };
  }
  const seq = range.nextSeq;
  return {
    ok: true,
    number: formatTerminalTicketNumber(range, seq),
    nextRange: { ...range, nextSeq: seq + 1 },
  };
}

/** Keep the higher nextSeq so offline mints aren't clobbered by a stale session. */
export function mergeNumberRange(
  server: TerminalNumberRange | null | undefined,
  local: TerminalNumberRange | null | undefined,
): TerminalNumberRange | null {
  if (!server && !local) return null;
  if (!server) return local ?? null;
  if (!local) return server;
  return {
    prefix: server.prefix || local.prefix,
    padWidth: server.padWidth || local.padWidth,
    rangeEnd: Math.max(server.rangeEnd, local.rangeEnd),
    nextSeq: Math.max(server.nextSeq, local.nextSeq),
  };
}

/**
 * Bump nextSeq past a number the server already allocated (e.g. online park
 * without a client mint). No-op when the string is not in this series.
 */
export function advanceNumberRangePast(
  range: TerminalNumberRange | null | undefined,
  allocatedNumber: string | null | undefined,
): TerminalNumberRange | null {
  if (!range) return null;
  const trimmed = allocatedNumber?.trim() ?? "";
  if (!trimmed.startsWith(range.prefix)) return range;
  const seqPart = trimmed.slice(range.prefix.length);
  if (!/^\d+$/.test(seqPart)) return range;
  const seq = Number(seqPart);
  if (!Number.isFinite(seq) || seq < 1) return range;
  const expected = formatTerminalTicketNumber(range, seq);
  if (trimmed !== expected) return range;
  return {
    ...range,
    nextSeq: Math.max(range.nextSeq, seq + 1),
  };
}
